<script lang="ts" setup>
    import { useDisplay } from 'vuetify';
    import { VForm } from 'vuetify/components';
    import DialogHeader from '@/components/DialogHeader.vue';
    import { userService } from '@/services/userService';

    const { t } = useI18n();
    const { mobile } = useDisplay();

    const isOpen = ref(false);
    const saving = ref(false);
    const formRef = ref<VForm>();

    const password = ref('');
    const newPassword = ref('');
    const confirmPassword = ref('');

    const rules = {
        required: (v: unknown) => (v != null && String(v).trim() !== '') || t('rules.required'),
        confirmPassword: (v: string) => !v || v === newPassword.value || t('rules.newPasswordDoesNotMatch'),
    };

    const open = (): void => {
        password.value = '';
        newPassword.value = '';
        confirmPassword.value = '';
        isOpen.value = true;
        void nextTick(() => formRef.value?.resetValidation());
    };

    defineExpose({ open });

    function close() {
        if (saving.value) return;
        isOpen.value = false;
    }

    async function onSave() {
        if (!formRef.value || saving.value) return;

        const { valid } = await formRef.value.validate();
        if (!valid) return;

        saving.value = true;
        try {
            const response = await userService.changePassword({
                password: password.value,
                newPassword: newPassword.value,
                newPassword_confirmation: confirmPassword.value,
            });
            if (response?.data?.success === false) return;
            isOpen.value = false;
        } finally {
            saving.value = false;
        }
    }
</script>
<template>
    <v-dialog
        v-model="isOpen"
        content-class="app-form-dialog"
        :max-width="mobile ? undefined : '420'"
        :fullscreen="mobile"
        :no-click-animation="true"
        persistent
        scrollable
        return-focus="false"
        @keydown.esc="close"
    >
        <v-card>
            <DialogHeader :title="t('menu.changePassword')" :show-search="false" @close="close" />

            <v-card-text class="pa-3">
                <v-form ref="formRef" @submit.prevent="onSave">
                    <input type="text" name="username" autocomplete="username" hidden />
                    <v-text-field
                        v-model="password"
                        :label="t('menu.currentPassword')"
                        type="password"
                        density="compact"
                        variant="outlined"
                        autocomplete="current-password"
                        class="app-form-input"
                        :rules="[rules.required]"
                    />
                    <v-text-field
                        v-model="newPassword"
                        :label="t('menu.password')"
                        type="password"
                        density="compact"
                        variant="outlined"
                        autocomplete="new-password"
                        class="app-form-input"
                        :rules="[rules.required]"
                    />
                    <v-text-field
                        v-model="confirmPassword"
                        :label="t('menu.confirmPassword')"
                        type="password"
                        density="compact"
                        variant="outlined"
                        autocomplete="new-password"
                        class="app-form-input"
                        :rules="[rules.required, rules.confirmPassword]"
                    />
                </v-form>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
                <v-spacer />
                <v-btn color="grey" variant="text" :disabled="saving" density="default" @click="close">{{ t('action.cancel') }}</v-btn>
                <v-btn color="tbGreen" style="color: white" variant="flat" elevation="0" density="default" :loading="saving" :disabled="saving" @click="onSave">
                    {{ t('action.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
